/**
 * "Accept the risk while this is fixed" — V109-1-19 (Medium, LM-15), reworked in round 3. The minimal
 * risk-acceptance create form, opened ONCE per list or page (R3 Medium: never on every row).
 * POST /api/w/:slug/risk-acceptances (owner / admin only: the caller renders it only for those roles,
 * and the route checks again).
 *
 *   which one (required)  when the list has more than one item without an acceptance
 *   scope (required)      one AI system of this workspace, or explicitly all systems (LM-15: a new
 *                         acceptance names its scope; nothing is preselected). For a connection problem
 *                         the options name the connection ("One system Google Cloud reads", R2-H5)
 *   justification         why the residual risk is acceptable (20+ characters, the route's rule)
 *   expiry                the last day it holds (at most 90 days out, the route's rule)
 *   risk owner            a member other than the caller (segregation of duties: the caller accepts)
 *
 * Accessibility (R2-H7): after a failed submit, focus moves to the first invalid field in an effect,
 * once the errors have rendered; one always-mounted polite summary says what needs fixing; each scope
 * radio is described by the scope error. Every field that is required is marked.
 *
 * An acceptance never hides an error: a source GAC Pro couldn't check stays Couldn't check until it
 * is fixed; the acceptance covers the judged results of what it names.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Field, Input, Select, Spinner, Textarea } from '@aigovops/design-system';
import { ApiError, createRiskAcceptance, getWorkspaceProfile, listWorkspaceMembers, listWorkspaceSystems } from '../lib/api';
import { dayText } from '../lib/couldnt-check';
import './couldnt-check.css';

const MAX_DAYS = 90;
const MIN_WHY = 20;
const dayPlus = (n: number) => new Date(Date.now() + n * 86_400_000).toISOString().slice(0, 10);

export interface AcceptRiskTarget {
  /** What is accepted, in words ("CC6.6 Access from outside the boundary restricted"). */
  label: string;
  /** The register item the acceptance records (the requirement or control id). */
  registerItemId: string;
  /** Narrow the acceptance to one control (optional). */
  controlId?: string | null;
  /** R3 (R2-H5): the connection that couldn't be read, when the problem is one connection (its name). */
  connection?: string | null;
}

type Key = 'target' | 'scope' | 'system' | 'why' | 'until' | 'owner';
const ORDER: readonly Key[] = ['target', 'scope', 'system', 'why', 'until', 'owner'];
const WORDS: Record<Key, string> = { target: 'which one', scope: 'what it covers', system: 'the system', why: 'why', until: 'the date', owner: 'the risk owner' };

export function AcceptRiskForm({ slug, viewerId, targets, onCancel, onDone }: {
  slug: string; viewerId: string; targets: readonly AcceptRiskTarget[]; onCancel: () => void;
  onDone: (message: string, target: AcceptRiskTarget) => void;
}) {
  const profile = useQuery({ queryKey: ['workspace-profile', slug], queryFn: () => getWorkspaceProfile(slug), staleTime: 60_000 });
  const systems = useQuery({ queryKey: ['ws-systems', slug], queryFn: () => listWorkspaceSystems(slug), staleTime: 60_000 });
  const members = useQuery({ queryKey: ['ws-members', slug], queryFn: () => listWorkspaceMembers(slug), staleTime: 60_000 });
  const [targetId, setTargetId] = useState(targets.length === 1 ? targets[0]!.registerItemId : '');
  const [scope, setScope] = useState<'system' | 'all' | ''>('');
  const [systemId, setSystemId] = useState('');
  const [why, setWhy] = useState('');
  const [until, setUntil] = useState(dayPlus(30));
  const [owner, setOwner] = useState('');
  const [errors, setErrors] = useState<Partial<Record<Key, string>>>({});
  const [focusTick, setFocusTick] = useState(0);
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => { heading.current?.focus(); }, []);
  const ownerOptions = (members.data?.members ?? []).filter((m) => m.user_id !== viewerId);
  const systemOptions = (systems.data?.systems ?? []).filter((s) => s.deployment_status !== 'retired');
  const target = targets.find((t) => t.registerItemId === targetId) ?? null;
  const conn = target?.connection ?? (targets.length === 1 ? targets[0]!.connection : null) ?? null;
  const base = `ar-${(targets[0]?.registerItemId ?? 'x').replace(/[^A-Za-z0-9-]/g, '-')}`;
  const id = (k: string) => `${base}-${k}`;
  const firstInvalid = ORDER.find((k) => errors[k]);

  // R2-H7: focus the first invalid field once the errors are on screen (never before they render).
  useEffect(() => {
    if (!focusTick || !firstInvalid) return;
    document.getElementById(firstInvalid === 'scope' ? id('scope-system') : id(firstInvalid))?.focus();
  }, [focusTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    if (busy) return;
    const next: Partial<Record<Key, string>> = {};
    if (!target) next.target = 'Choose which one this covers.';
    if (!scope) next.scope = 'Choose one system or all systems.';
    else if (scope === 'system' && !systemId) next.system = 'Choose the system this covers.';
    if (why.trim().length < MIN_WHY) next.why = `Say why the risk is acceptable (at least ${MIN_WHY} characters).`;
    if (!(until > dayPlus(0)) || until > dayPlus(MAX_DAYS)) next.until = `Choose a date after today and within ${MAX_DAYS} days.`;
    if (!owner) next.owner = 'Choose who owns the risk.';
    setErrors(next);
    if (Object.keys(next).length) { setFocusTick((n) => n + 1); return; }
    const profileId = profile.data?.profile?.profile_id;
    if (!profileId) { setFormError('Finish onboarding first: this workspace has no profile to record the acceptance on.'); return; }
    setBusy(true); setFormError(null);
    try {
      await createRiskAcceptance(slug, {
        register_item_id: target!.registerItemId, profile_id: profileId, owner_user_id: owner,
        target_closure_at: until, residual_risk_rationale: why.trim(),
        ...(target!.controlId ? { control_id: target!.controlId } : {}),
        ...(scope === 'system' ? { system_id: systemId } : { all_systems: true as const }),
      });
      const ownerName = ownerOptions.find((m) => m.user_id === owner)?.display_name ?? 'the risk owner';
      onDone(`Risk accepted until ${dayText(until)} by ${ownerName}. It stays “Couldn't check” until fixed.`, target!);
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { errors?: Array<{ path: string; message: string }>; detail?: string; error?: string } | undefined) : undefined;
      const fieldErrs: Partial<Record<Key, string>> = {};
      for (const fe of body?.errors ?? []) {
        if (fe.path === 'system_id' || fe.path === 'all_systems') fieldErrs.scope = fe.message;
        else if (fe.path === 'owner_user_id') fieldErrs.owner = fe.message;
        else if (fe.path === 'target_closure_at') fieldErrs.until = fe.message;
        else if (fe.path === 'residual_risk_rationale') fieldErrs.why = fe.message;
      }
      setErrors(fieldErrs);
      if (Object.keys(fieldErrs).length) setFocusTick((n) => n + 1);
      else setFormError(body?.detail ?? body?.error ?? (err instanceof Error ? err.message : 'That did not save. Try again.'));
    } finally { setBusy(false); }
  };

  const loading = profile.isLoading || systems.isLoading || members.isLoading;
  const invalid = ORDER.filter((k) => errors[k]);
  const scopeDesc = errors.scope ? id('scope-err') : undefined;
  return (
    <form className="cc-accept" onSubmit={submit} noValidate aria-labelledby={id('h')} data-part="accept-risk-form"
      onKeyDown={(ev) => { if (ev.key === 'Escape') { ev.stopPropagation(); onCancel(); } }}>
      <h3 id={id('h')} ref={heading} tabIndex={-1} className="cc-accept-h">Accept the risk while this is fixed</h3>
      {targets.length === 1 ? <p className="cc-accept-sub">{targets[0]!.label}</p> : null}
      {/* R2-H7: one summary, always mounted, polite — it speaks once per failed submit. */}
      <p className="cc-err-summary" role="status" aria-live="polite" data-part="accept-risk-errors">
        {invalid.length ? `Check ${invalid.length === 1 ? 'one field' : `${invalid.length} fields`}: ${invalid.map((k) => WORDS[k]).join(', ')}.` : ''}
      </p>
      {loading ? <Spinner label="Loading" /> : <>
        {formError ? <Alert variant="error">{formError}</Alert> : null}
        {targets.length > 1 ? (
          <Field id={id('target')} label="Which one" required error={errors.target || undefined} errorRole="none">
            <Select value={targetId} onChange={(e) => { setTargetId(e.target.value); setErrors((x) => ({ ...x, target: '' })); }}>
              <option value="">Choose one</option>
              {targets.map((t) => <option key={t.registerItemId} value={t.registerItemId}>{t.label}</option>)}
            </Select>
          </Field>
        ) : null}
        <fieldset className="cc-accept-scope" aria-describedby={scopeDesc} aria-required="true">
          <legend>Covers<span aria-hidden="true" className="cc-req"> *</span><span className="sr-only"> (required)</span></legend>
          <label className="cc-radio"><input id={id('scope-system')} type="radio" name={id('scope')} checked={scope === 'system'} aria-invalid={errors.scope ? true : undefined}
            aria-describedby={scopeDesc} onChange={() => { setScope('system'); setErrors((x) => ({ ...x, scope: '' })); }} />{conn ? `One system ${conn} reads` : 'One system'}</label>
          <label className="cc-radio"><input id={id('scope-all')} type="radio" name={id('scope')} checked={scope === 'all'} aria-invalid={errors.scope ? true : undefined}
            aria-describedby={scopeDesc} onChange={() => { setScope('all'); setErrors((x) => ({ ...x, scope: '', system: '' })); }} />{conn ? `All systems ${conn} reads` : 'All systems'}</label>
          {errors.scope ? <p className="cc-err" id={id('scope-err')}>{errors.scope}</p> : null}
        </fieldset>
        {scope === 'system' ? (
          systemOptions.length
            ? <Field id={id('system')} label="System" required error={errors.system || undefined} errorRole="none">
                <Select value={systemId} onChange={(e) => setSystemId(e.target.value)}>
                  <option value="">Choose a system</option>
                  {systemOptions.map((s) => <option key={s.system_id} value={s.system_id}>{s.name}</option>)}
                </Select>
              </Field>
            : <p className="cc-err">No systems yet. Choose all systems, or add a system first.</p>
        ) : null}
        <Field id={id('why')} label="Why the risk is acceptable" required hint={`At least ${MIN_WHY} characters. Auditors read this.`} error={errors.why || undefined} errorRole="none">
          <Textarea rows={3} maxLength={2000} value={why} onChange={(e) => setWhy(e.target.value)} />
        </Field>
        <div className="cc-accept-row">
          <Field id={id('until')} label="Holds until" required hint={`At most ${MAX_DAYS} days.`} error={errors.until || undefined} errorRole="none">
            <Input type="date" value={until} min={dayPlus(1)} max={dayPlus(MAX_DAYS)} onChange={(e) => setUntil(e.target.value)} />
          </Field>
          <Field id={id('owner')} label="Risk owner" required hint="Someone other than you: you accept it." error={errors.owner || undefined} errorRole="none">
            <Select value={owner} onChange={(e) => setOwner(e.target.value)}>
              <option value="">Choose a person</option>
              {ownerOptions.map((m) => <option key={m.user_id} value={m.user_id}>{m.display_name}</option>)}
            </Select>
          </Field>
        </div>
        <div className="cc-accept-acts">
          <Button type="submit" aria-disabled={busy || undefined}>{busy ? 'Saving…' : 'Accept the risk'}</Button>
          <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        </div>
      </>}
    </form>
  );
}
